<div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
  <div class="flex flex-col bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[85vh] overflow-hidden">
    
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100">
      <div class="flex flex-col">
        <h2 class="text-lg font-bold text-slate-800">Fix image</h2>
        <p class="text-xs font-semibold text-slate-500">{{ fixReason }}</p>
      </div>
      <div class="text-xs font-bold px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
        {{ config.type.replace('_', ' ') }}
      </div>
    </div>

    <!-- Body -->
    <div class="flex flex-1 overflow-hidden flex-col md:flex-row">
      <!-- Left: Editor Workspace -->
      <div class="flex-1 min-w-0 min-h-0 flex flex-col items-center justify-center bg-slate-50 relative p-4 md:p-8 border-b md:border-b-0 md:border-r border-slate-200">
        <!-- Viewport Container -->
        <div 
          #viewport
          class="relative max-w-full max-h-full shrink flex items-center justify-center"
          style="width: 100%; height: 100%;"
          [style.aspectRatio]="containerAspect"
        >
          <!-- Bounding box enforcing aspect ratio -->
          <div 
            class="relative overflow-hidden w-full h-full rounded shadow-sm border border-slate-200/50"
            [ngClass]="{
              'transparent-checkerboard': state.backgroundMode === 'transparent',
              'bg-white': state.backgroundMode === 'white'
            }"
          >
            <!-- The Draggable Image -->
            <img 
              [src]="originalUrl" 
              class="absolute top-0 left-0 cursor-move will-change-transform"
              [ngStyle]="imageStyle"
              cdkDrag
              (cdkDragEnded)="onDragEnded($event)"
              alt="Source Editor Target"
            />
          </div>
        </div>

        <!-- Inline Floating Controls -->
        <div class="absolute bottom-6 flex items-center gap-2 bg-white/90 backdrop-blur-md shadow-md rounded-full px-4 py-2 border border-slate-200/60">
          <button type="button" class="text-slate-600 hover:text-slate-900 p-2" (click)="rotate()" title="Rotate 90">
            <i class="fi fi-rs-rotate-right font-bold text-lg leading-none"></i>
          </button>
          <div class="w-px h-6 bg-slate-200 mx-1"></div>
          <button type="button" class="text-slate-600 hover:text-slate-900 p-2" (click)="zoomOut()" [disabled]="state.zoom <= minZoom" [class.opacity-50]="state.zoom <= minZoom">
            <i class="fi fi-rs-zoom-out font-bold text-lg leading-none"></i>
          </button>
          <span class="text-xs font-bold text-slate-500 w-12 text-center">{{ state.zoom | percent:'1.0-0' }}</span>
          <button type="button" class="text-slate-600 hover:text-slate-900 p-2" (click)="zoomIn()">
            <i class="fi fi-rs-zoom-in font-bold text-lg leading-none"></i>
          </button>
        </div>
      </div>

      <!-- Right: Info Panel -->
      <div class="w-full md:w-80 flex flex-col bg-white overflow-y-auto">
        <div class="p-5 flex flex-col gap-6">
          
          <!-- Required Panel -->
          <div class="flex flex-col gap-2">
            <h3 class="text-[10px] font-black uppercase text-slate-400 tracking-wider">Required</h3>
            <div class="flex flex-col text-xs font-medium text-slate-600 gap-1 bg-slate-50 rounded-lg p-3 border border-slate-100">
              <div class="flex justify-between"><span>Dimensions</span> <span class="font-bold text-slate-800">{{ config.recommendedWidth }} × {{ config.recommendedHeight }} px</span></div>
              <div class="flex justify-between"><span>Ratio</span> <span class="font-bold text-slate-800">{{ config.targetRatio === 1 ? '1:1' : config.targetRatio + ':1' }}</span></div>
              <div class="flex justify-between"><span>Max Size</span> <span class="font-bold text-slate-800">{{ config.maxFilesizeKb }} KB</span></div>
              <div class="flex justify-between"><span>Fit</span> <span class="font-bold text-slate-800 capitalize">{{ config.fitMode }}</span></div>
              <div class="flex justify-between"><span>Background</span> <span class="font-bold text-slate-800 capitalize">{{ config.backgroundMode }}</span></div>
            </div>
          </div>

          <!-- Original Panel -->
          <div class="flex flex-col gap-2">
            <h3 class="text-[10px] font-black uppercase text-slate-400 tracking-wider">Original Upload</h3>
            <div class="flex flex-col text-xs font-medium text-slate-600 gap-1">
              <div class="flex justify-between"><span>Dimensions</span> <span class="font-bold text-slate-800">{{ metadata.width }} × {{ metadata.height }} px</span></div>
              <div class="flex justify-between"><span>Ratio</span> <span class="font-bold text-slate-800">{{ metadata.aspectRatio | number:'1.1-2' }}:1</span></div>
              <div class="flex justify-between"><span>Size</span> <span class="font-bold text-slate-800">{{ (metadata.fileSizeBytes / 1024) | number:'1.0-0' }} KB</span></div>
              <div class="flex justify-between"><span>Format</span> <span class="font-bold text-slate-800">{{ metadata.mimeType.split('/')[1] }}</span></div>
            </div>
          </div>

          <!-- Live Output Panel -->
          <div class="flex flex-col gap-2">
            <h3 class="text-[10px] font-black uppercase text-slate-400 tracking-wider flex items-center gap-2">
              Output Simulation
              @if(hasUnsavedChanges) {<span class="w-2 h-2 rounded-full bg-amber-400"></span>}
            </h3>
            <div class="flex flex-col text-xs font-medium text-slate-600 gap-1 bg-emerald-50 rounded-lg p-3 border border-emerald-100">
              <div class="flex justify-between"><span>Dimensions</span> <span class="font-bold text-emerald-800">{{ config.recommendedWidth }} × {{ config.recommendedHeight }} px</span></div>
              <div class="flex justify-between"><span>Ratio</span> <span class="font-bold text-emerald-800">{{ config.targetRatio === 1 ? '1:1' : config.targetRatio + ':1' }}</span></div>
              <div class="flex justify-between"><span>Format</span> <span class="font-bold text-emerald-800 uppercase">{{ outputFormat }}</span></div>
              <div class="flex justify-between">
                <span>Quality</span> 
                <span class="font-bold" [ngClass]="qualityStatusText.includes('limit reached') ? 'text-amber-600' : 'text-emerald-800'">{{ qualityStatusText }}</span>
              </div>
            </div>
          </div>

          <!-- Background Toggles -->
          @if (config.allowPadding) {
            <div class="flex flex-col gap-2 mt-2">
              <span class="text-[10px] font-black uppercase text-slate-400 tracking-wider">Padding Color</span>
              <div class="flex bg-slate-100 p-1 rounded-lg gap-1">
                <button type="button" class="flex-1 text-xs font-bold py-1.5 rounded-md transition-colors"
                  [class.bg-white]="state.backgroundMode === 'transparent'"
                  [class.shadow-sm]="state.backgroundMode === 'transparent'"
                  [class.text-slate-800]="state.backgroundMode === 'transparent'"
                  [class.text-slate-500]="state.backgroundMode !== 'transparent'"
                  (click)="toggleBackground('transparent')">
                  Transparent
                </button>
                <button type="button" class="flex-1 text-xs font-bold py-1.5 rounded-md transition-colors border border-transparent"
                  [class.bg-white]="state.backgroundMode === 'white'"
                  [class.shadow-sm]="state.backgroundMode === 'white'"
                  [class.text-slate-800]="state.backgroundMode === 'white'"
                  [class.text-slate-500]="state.backgroundMode !== 'white'"
                  (click)="toggleBackground('white')">
                  Solid White
                </button>
              </div>
            </div>
          }
          
          <!-- Errors in processing -->
          @if (processingError) {
            <div class="p-3 bg-red-50 text-red-600 text-xs font-semibold rounded-lg border border-red-200">
              <i class="fi fi-rs-exclamation mr-1"></i> {{ processingError }}
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
      <div class="flex items-center gap-2">
        <button type="button" class="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors" (click)="cancel()">
          Cancel
        </button>
      </div>
      
      <div class="flex items-center gap-3">
        <button type="button" class="px-4 py-2 text-sm font-bold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-100" (click)="autoFix()">
          Auto Fix
        </button>
        <button 
          type="button" 
          class="px-5 py-2 text-sm font-black text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-md transition-colors flex items-center gap-2" 
          (click)="useImage()"
          [disabled]="isProcessing"
          [class.opacity-70]="isProcessing"
        >
          @if (isProcessing) {
            <i class="fi fi-rs-spinner-alt animate-spin"></i> Processing...
          } @else {
            Use this image <i class="fi fi-rs-arrow-right mt-0.5"></i>
          }
        </button>
      </div>
    </div>

  </div>
</div>
